import React, { useState, useMemo } from "react";
import styled from "styled-components";
import * as d3 from "d3";

import DistributionPlot from "common/graphs/DistributionPlot";
import Switch from "@mui/material/Switch";

const Container = styled.div`
  width: 100%;
`;

const SelectWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  justify-content: right;
  margin-right: 35px;
  @media (max-width: 900px) {
    flex-wrap: wrap;
  }
`;

const SwitchWithLabel = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const Label = styled.div`
  margin-right: 10px;
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
  color: var(--Text-Primary);
  width: 100px;
  text-align: right;
`;

const MetricHeader = styled.div`
  font-size: 13px !important;
  padding-top: 0px;
  width: 100%;
  text-align: center;
  font-family: "AllianceNo2-SemiBold" !important;
  padding: 0px 0px 0 240px;
  color: var(--Text-Primary);
  white-space: nowrap;

  @media (max-width: 900px) {
    padding: 0px 40px 0 240px;
  }
`;

const HeaderChart = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: 900px) {
    display: block;
  }
`;

const emptyItem = {
  p10: "n/a",
  p25: "n/a",
  p50: "n/a",
  p75: "n/a",
  p90: "n/a",
  min: "n/a",
  max: "n/a",
};

function BoxPlotSubscribers({
  items,
  fieldsSet,
  selection,
  setSelection,
  onContextClick,
  unit,
  fields,
  filterMax,
  filterMin,
  metric,
}) {
  const [showMinMax, setShowMinMax] = useState(false);
  const [plotMode, setPlotMode] = useState("boxplot");

  const fieldsQuantileData = useMemo(() => {
    const fieldsValues = items.reduce((acc, value) => {
      Object.entries(value).forEach(([key, value]) => {
        if (
          key !== "time" &&
          key !== "DOWNLINK" &&
          key !== "UPLINK" &&
          key !== "network" &&
          value !== "n/a" &&
          value
        ) {
          if (!acc[key]) {
            acc[key] = [value];
          } else {
            acc[key].push(value);
          }
        }
      });

      return acc;
    }, {});

    return Object.entries(fieldsValues).reduce((acc, [key, value]) => {
      if (value.length === 0) {
        acc.push({ ...emptyItem, app: key });
        return acc;
      }
      const filteredValues = value.filter((item) => {
        if (!filterMax && !filterMin) {
          return true;
        }
        if (filterMax && filterMin) {
          return item >= filterMin && item <= filterMax;
        }
        if (filterMax) {
          return item <= filterMax;
        }
        if (filterMin) {
          return item >= filterMin;
        }
        return true;
      });

      if (filteredValues.length === 0) {
        acc.push({ ...emptyItem, app: key });
        return acc;
      }

      const sortedValues = [...filteredValues].sort(function (a, b) {
        return a - b;
      });
      const p25 = d3.quantile(sortedValues, 0.25);
      const p50 = d3.quantile(sortedValues, 0.5);
      const p75 = d3.quantile(sortedValues, 0.75);
      const min = sortedValues.at(0);
      const max = sortedValues.at(-1);
      const p10 = d3.quantile(sortedValues, 0.1);
      const p90 = d3.quantile(sortedValues, 0.9);
      acc.push({ p10, p25, p50, p75, p90, min, max, app: key });

      return acc;
    }, []);
  }, [
    fields.map((field) => field.name).join(","),
    items,
    filterMax,
    filterMin,
  ]);

  return (
    <Container>
      <HeaderChart>
      <MetricHeader>{`${metric.toUpperCase()} (${unit})`} </MetricHeader>
      <SelectWrapper>
        <SwitchWithLabel>
          <Label>Line chart view</Label>{" "}
          <Switch
            className="sw-input"
            checked={plotMode !== "boxplot"}
            onChange={() =>
              setPlotMode(plotMode === "boxplot" ? "line" : "boxplot")
            }
          />
        </SwitchWithLabel>
        <SwitchWithLabel>
          <Label>View max+min</Label>{" "}
          <Switch
            className="sw-input"
            checked={showMinMax}
            onChange={() => setShowMinMax(!showMinMax)}
          />
        </SwitchWithLabel>
      </SelectWrapper>
      </HeaderChart>
      <DistributionPlot
        plotMode={plotMode}
        data={fieldsQuantileData}
        fields={fields}
        margin={{ top: 10, right: 45, bottom: 70, left: 240 }}
        minmax={showMinMax}
        yAxisUnit={unit}
        selection={selection}
        setSelection={setSelection}
        onContextClick={onContextClick}
      />
    </Container>
  );
}

export default BoxPlotSubscribers;
